import { useMemo, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/feature/PageHeader";
import WorkCard from "@/components/base/WorkCard";
import useSeo from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";
import { works, type WorkItem } from "@/mocks/works";

const matchMap: Record<string, (w: WorkItem) => boolean> = {
  new: (w) => w.type === "new",
  movie: (w) => w.type === "movie",
  ova: (w) => w.type === "ova",
  classic: (w) => w.type === "classic",
  "hot-blooded": (w) => w.category === "热血",
  healing: (w) => w.category === "治愈",
  "sci-fi": (w) => w.category === "科幻",
  adventure: (w) => w.category === "冒险",
};

type SortKey = "score" | "year";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = categories.find((c) => c.slug === slug);
  const [sort, setSort] = useState<SortKey>("score");

  const list = useMemo(() => {
    const matcher = matchMap[slug] || (() => false);
    const filtered = works.filter(matcher);
    return [...filtered].sort((a, b) =>
      sort === "score" ? b.score - a.score : b.year - a.year
    );
  }, [slug, sort]);

  useSeo({
    title: category
      ? `${category.name} - ${category.subtitle} | 漫番志`
      : "分类页 | 漫番志",
    description: category
      ? `${category.description} 收录口碑${category.name}动漫作品，评分排行一目了然，帮你快速找到值得追的${category.name}。`
      : "漫番志动漫分类导航",
    keywords: `动漫${category?.name || ""},${category?.name || ""}番剧,动漫排行,追番`,
    jsonLd: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://freshanime.github.io/" },
              { "@type": "ListItem", position: 2, name: category.name },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: category.name,
            description: category.description,
          },
        ]
      : undefined,
  });

  if (!category) return <Navigate to="/" replace />;

  return (
    <Layout>
      <PageHeader
        title={category.name}
        subtitle={category.description}
        eyebrow={category.subtitle}
        crumbs={[{ label: category.name }]}
      />

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground-600">
            共收录 <span className="font-bold text-primary-400">{list.length}</span> 部作品，按
            {sort === "score" ? "评分" : "年份"}排序
          </p>
          <div className="inline-flex rounded-full bg-background-100 p-1 ring-1 ring-background-300/60">
            {(
              [
                { key: "score", label: "评分优先" },
                { key: "year", label: "年份最新" },
              ] as { key: SortKey; label: string }[]
            ).map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSort(s.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  sort === s.key
                    ? "bg-primary-500 font-bold text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {list.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl bg-background-100 py-20 text-center ring-1 ring-background-300/60">
            <i className="ri-inbox-line text-4xl text-foreground-500" />
            <p className="mt-4 text-sm text-foreground-600">该分类下暂未收录作品，敬请期待。</p>
          </div>
        )}
      </div>
    </Layout>
  );
}